import { adminFetch } from './staffAuth';
import type { StaffMe, StaffPermissions } from '../types/auth';
import type {
  CatalogRecord,
  CatalogWritePayload,
  ClientRecord,
  ManufacturedRecord,
  ManufacturedTelemetryHealth,
  ManufacturingBatch,
  ManufacturingBatchStatus,
} from '../types/registry';

export async function fetchMe(): Promise<StaffMe> {
  return adminFetch<StaffMe>('/me');
}

export async function updateMe(body: {
  display_name?: string;
  phone?: string;
  address?: string;
  birth_date?: string;
}): Promise<{ item: StaffMe }> {
  return adminFetch('/me', { method: 'PUT', body: JSON.stringify(body) });
}

export async function requestPhotoUploadUrl(contentType: string): Promise<{
  upload_url: string;
  photo_url: string;
  photo_object_key: string;
}> {
  return adminFetch('/me/photo-upload-url', {
    method: 'POST',
    body: JSON.stringify({ content_type: contentType }),
  });
}

export async function listClients(): Promise<ClientRecord[]> {
  const data = await adminFetch<{ items: ClientRecord[] }>('/clients');
  return data.items ?? [];
}

export async function createClient(body: Partial<ClientRecord>): Promise<ClientRecord> {
  const data = await adminFetch<{ item: ClientRecord }>('/clients', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.item;
}

export async function onboardClient(body: {
  display_name: string;
  client_type: string;
  master_email: string;
  address: string;
  nit: string;
  city: string;
  country: string;
}): Promise<{
  ok: boolean;
  client_id?: string;
  master_email?: string;
  cognito_username?: string;
  client_type?: string;
  setup_kind?: string;
}> {
  return adminFetch('/clients/onboard', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function resetPartnerPassword(clientId: string): Promise<{ ok: boolean }> {
  return adminFetch(`/clients/${encodeURIComponent(clientId)}/password-reset`, {
    method: 'POST',
    body: '{}',
  });
}

export async function updateClient(
  clientId: string,
  body: Partial<ClientRecord>,
): Promise<ClientRecord> {
  const data = await adminFetch<{ item: ClientRecord }>(`/clients/${encodeURIComponent(clientId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return data.item;
}

export interface CoreUserRecord {
  user_id: string;
  cognito_username?: string;
  cognito_user_id?: string;
  client_id?: string;
  role?: string;
  status?: string;
  alias?: string;
}

export async function syncClientMaster(clientId: string): Promise<{
  master_id: string;
  user: CoreUserRecord | null;
  synced: boolean;
}> {
  const data = await adminFetch<{
    master_id: string;
    user: CoreUserRecord | null;
    synced: boolean;
  }>(`/clients/${encodeURIComponent(clientId)}/sync-master`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return {
    master_id: data.master_id,
    user: data.user ?? null,
    synced: Boolean(data.synced),
  };
}

export async function getCoreUser(userId: string): Promise<CoreUserRecord> {
  const data = await adminFetch<{ item: CoreUserRecord }>(
    `/users/${encodeURIComponent(userId)}`,
  );
  return data.item;
}

export interface CatalogListResult {
  items: CatalogRecord[];
  categories: string[];
  manufacturers: string[];
  models: string[];
}

export async function listCatalog(): Promise<CatalogListResult> {
  const data = await adminFetch<CatalogListResult>('/device-catalog');
  return {
    items: data.items ?? [],
    categories: data.categories ?? [],
    manufacturers: data.manufacturers ?? [],
    models: data.models ?? [],
  };
}

export async function createCatalog(body: CatalogWritePayload): Promise<CatalogRecord> {
  const data = await adminFetch<{ item: CatalogRecord }>('/device-catalog', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.item;
}

export async function updateCatalog(
  typeId: string,
  body: Partial<CatalogWritePayload>,
): Promise<CatalogRecord> {
  const data = await adminFetch<{ item: CatalogRecord }>(
    `/device-catalog/${encodeURIComponent(typeId)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
  return data.item;
}

export async function listManufactured(batchId?: string): Promise<ManufacturedRecord[]> {
  const q = batchId ? `?batch_id=${encodeURIComponent(batchId)}` : '';
  const data = await adminFetch<{ items: ManufacturedRecord[] }>(`/manufactured-devices${q}`);
  return data.items ?? [];
}

export async function getManufactured(mac: string): Promise<ManufacturedRecord> {
  const data = await adminFetch<{ item: ManufacturedRecord }>(
    `/manufactured-devices/${encodeURIComponent(mac)}`,
  );
  return data.item;
}

export interface ManufacturedProvisioningDetail {
  ok: boolean;
  mac: string;
  manufacturing: {
    provisioning_status?: string;
    iot_thing_name?: string;
    iot_provisioned_at?: number;
    current_client_id?: string;
    last_client_id?: string;
    last_client_bound_at?: number;
  };
  registry: {
    device_id?: string;
    client_id?: string;
    status?: string;
    provisioning_status?: string;
    iot_thing_name?: string;
    provisioned_at?: number;
    mac?: string;
  } | null;
  iot: {
    thing_name: string;
    exists: boolean | null;
    error?: string;
  };
  registry_error?: string;
}

export async function getManufacturedProvisioning(
  mac: string,
): Promise<ManufacturedProvisioningDetail> {
  return adminFetch<ManufacturedProvisioningDetail>(
    `/manufactured-devices/${encodeURIComponent(mac)}/provisioning`,
  );
}

export async function syncManufacturedIot(mac: string): Promise<{
  ok: boolean;
  mac: string;
  manufacturing: ManufacturedProvisioningDetail['manufacturing'];
  iot: ManufacturedProvisioningDetail['iot'];
}> {
  return adminFetch(`/manufactured-devices/${encodeURIComponent(mac)}/sync-iot`, {
    method: 'POST',
    body: '{}',
  });
}

export async function resetManufacturedIot(mac: string): Promise<{
  ok: boolean;
  mac: string;
  teardown: {
    thing_name: string;
    thing_existed: boolean;
    thing_deleted?: boolean;
    certs_deleted: number;
    policies_detached: number;
  };
  manufacturing: ManufacturedProvisioningDetail['manufacturing'];
  core_rows_updated: number;
}> {
  return adminFetch(`/manufactured-devices/${encodeURIComponent(mac)}/reset-iot`, {
    method: 'POST',
    body: '{}',
  });
}

export async function resetManufacturedClient(mac: string): Promise<{
  ok: boolean;
  mac: string;
  manufacturing: ManufacturedProvisioningDetail['manufacturing'];
  core_rows_updated: number;
}> {
  return adminFetch(`/manufactured-devices/${encodeURIComponent(mac)}/reset-client`, {
    method: 'POST',
    body: '{}',
  });
}

export async function getManufacturedTelemetryHealth(
  mac: string,
): Promise<ManufacturedTelemetryHealth> {
  return adminFetch(`/manufactured-devices/${encodeURIComponent(mac)}/telemetry-health`);
}

export async function listBatches(): Promise<ManufacturingBatch[]> {
  const data = await adminFetch<{ items: ManufacturingBatch[] }>('/manufacturing-batches');
  return data.items ?? [];
}

export async function createBatch(body: {
  batch_date: string;
  batch_size: number;
  type_ids: string[];
  notes?: string;
  status?: ManufacturingBatchStatus;
}): Promise<ManufacturingBatch> {
  const data = await adminFetch<{ item: ManufacturingBatch }>('/manufacturing-batches', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.item;
}

export async function updateBatch(
  batchId: string,
  body: {
    batch_date?: string;
    batch_size?: number;
    status?: ManufacturingBatchStatus;
    notes?: string;
    type_ids?: string[];
  },
): Promise<ManufacturingBatch> {
  const data = await adminFetch<{ item: ManufacturingBatch }>(
    `/manufacturing-batches/${encodeURIComponent(batchId)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
  return data.item;
}

export async function createManufactured(
  body: Partial<ManufacturedRecord>,
): Promise<ManufacturedRecord> {
  const data = await adminFetch<{ item: ManufacturedRecord }>('/manufactured-devices', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.item;
}

export type ManufacturedBatchCreateResult = {
  ok: boolean;
  created_count: number;
  failed_count: number;
  failed_macs: string[];
};

export async function createManufacturedBatch(body: {
  macs: string[];
  batch_id: string;
  type_ids: string[];
  status?: string;
  provisioning_status?: string;
  notes?: string;
}): Promise<ManufacturedBatchCreateResult> {
  return adminFetch<ManufacturedBatchCreateResult>('/manufactured-devices', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateManufactured(
  mac: string,
  body: Partial<ManufacturedRecord>,
): Promise<ManufacturedRecord> {
  const data = await adminFetch<{ item: ManufacturedRecord }>(
    `/manufactured-devices/${encodeURIComponent(mac)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
  return data.item;
}

export async function listStaff(): Promise<StaffMe[]> {
  const data = await adminFetch<{ items: StaffMe[] }>('/staff');
  return data.items ?? [];
}

export async function inviteStaff(body: {
  email: string;
  display_name?: string;
  role?: 'admin' | 'operator';
  permissions?: StaffPermissions;
}): Promise<{ staff_user_id: string; email: string; role: string; permissions: StaffPermissions }> {
  return adminFetch('/staff', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateStaff(
  staffUserId: string,
  body: {
    display_name?: string;
    role?: 'admin' | 'operator';
    status?: 'ACTIVE' | 'DISABLED';
    permissions?: StaffPermissions;
  },
): Promise<StaffMe> {
  const data = await adminFetch<{ item: StaffMe }>(
    `/staff/${encodeURIComponent(staffUserId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
  return data.item;
}

export async function deleteStaff(staffUserId: string): Promise<void> {
  await adminFetch(`/staff/${encodeURIComponent(staffUserId)}`, { method: 'DELETE' });
}
